import { FC, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import cn from 'classnames';
import { Button } from '@ebsco-ui/ebsco-ui';

import {
  FeatureFlags,
  getErrorNotificationProps,
  Icon,
  ModalNotification,
} from '@app/components';
import { useHoldsAndCheckoutsPageConfig } from '@app/hooks';
import { getItemStatusPanelMessages } from '@app/translations';
import {
  FEATURE,
  HOLDS_AND_CHECKOUTS_TABS,
  ITEM_STATUS,
  ItemStatusData,
  SetState,
} from '@app/constants';

import css from '../ItemStatusPanel.module.scss';

interface ItemStatusDataProps {
  itemStatusData: ItemStatusData;
  errorMessage?: string;
  isRemoveHoldError?: boolean;
  onModalOpen: SetState<boolean>;
  onErrorClose: () => void;
}

export const ItemStatusPanel: FC<ItemStatusDataProps> = ({
  itemStatusData: {
    icon,
    isNonCatalogItem,
    status,
    statusMessage,
    actionButtonText,
    feesTitle,
    feesValue,
    isVirtualItem,
  },
  errorMessage = '',
  isRemoveHoldError,
  onModalOpen,
  onErrorClose,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { $t } = useIntl();
  const { holdsAndCheckoutsRoute, getTabRoute, selectConfiguredOption } =
    useHoldsAndCheckoutsPageConfig();
  const statusMessages = getItemStatusPanelMessages(selectConfiguredOption);

  const link = getTabRoute(
    status === ITEM_STATUS.onHold
      ? HOLDS_AND_CHECKOUTS_TABS.holds
      : HOLDS_AND_CHECKOUTS_TABS.checkouts
  );
  const isHoldsAndCheckoutsRoute = location.pathname.startsWith(
    holdsAndCheckoutsRoute
  );

  const handleButtonClick = useCallback(() => {
    if (isHoldsAndCheckoutsRoute) {
      onModalOpen(true);
    } else {
      navigate(link);
    }
  }, [navigate, link, isHoldsAndCheckoutsRoute, onModalOpen]);

  return (
    <div className={css.wrapper} data-auto="status-panel">
      <div className={css.statusWrapper}>
        <Icon icon={icon} className={cn(css.icon, css[icon.iconName])}>
          <div className={css.statusInfo}>
            <span>
              {isNonCatalogItem || isVirtualItem ? (
                <strong data-auto="status-title">
                  {isNonCatalogItem
                    ? $t(statusMessages.feeDue)
                    : $t(statusMessages[status])}
                </strong>
              ) : (
                <>
                  <strong data-auto="status-title">
                    {`${$t(statusMessages[status])}: `}
                  </strong>
                  <span data-auto="status-message">{statusMessage}</span>
                </>
              )}
            </span>
            {feesValue && (
              <span>
                <strong data-auto="fee-title">{`${feesTitle}: `}</strong>
                <span data-auto="fee-message">{feesValue}</span>
              </span>
            )}
          </div>
        </Icon>
      </div>
      {!isNonCatalogItem && (
        <FeatureFlags
          flagsToMatch={[
            { name: FEATURE.holdsAndCheckouts, isActiveIfNotFound: true },
          ]}
        >
          <div className={css.buttonsWrapper}>
            <Button styleType="link" onClick={handleButtonClick}>
              {actionButtonText}
            </Button>
          </div>
        </FeatureFlags>
      )}
      {isRemoveHoldError && (
        <div className={css.errorWrapper}>
          <ModalNotification
            type="error"
            modalNotifications={{
              error: getErrorNotificationProps({
                title: selectConfiguredOption(
                  <FormattedMessage
                    id="itemStatus.unableToRemoveRequest"
                    defaultMessage="We were unable to remove this request."
                  />,
                  <FormattedMessage
                    id="itemStatus.unableToRemovelHold"
                    defaultMessage="We were unable to remove this hold."
                  />
                ),
                message: errorMessage,
              }),
            }}
            onCloseButtonClick={onErrorClose}
          />
        </div>
      )}
    </div>
  );
};
